import { apiClient, API_ENDPOINTS } from '../config';
import {
  ApiResponse,
  Settings,
  ApiKey,
} from '../types';

export class SettingsService {
  // Get system settings
  static async getSettings(): Promise<Settings> {
    const response = await apiClient.get<ApiResponse<Settings>>(API_ENDPOINTS.settings.get);
    return response.data.data;
  }

  // Update system settings
  static async updateSettings(settings: Partial<Settings>): Promise<Settings> {
    const response = await apiClient.patch<ApiResponse<Settings>>(
      API_ENDPOINTS.settings.update,
      settings
    );
    return response.data.data;
  }

  // Get API keys
  static async getApiKeys(): Promise<ApiKey[]> {
    const response = await apiClient.get<ApiResponse<ApiKey[]>>(API_ENDPOINTS.settings.apiKeys);
    return response.data.data;
  }

  // Create API key
  static async createApiKey(data: {
    name: string;
    permissions: string[];
    expires_at?: string;
  }): Promise<ApiKey> {
    const response = await apiClient.post<ApiResponse<ApiKey>>(
      API_ENDPOINTS.settings.apiKeys,
      data
    );
    return response.data.data;
  }

  // Update API key
  static async updateApiKey(id: string, data: {
    name?: string;
    permissions?: string[];
    is_active?: boolean;
    expires_at?: string;
  }): Promise<ApiKey> {
    const response = await apiClient.patch<ApiResponse<ApiKey>>(
      `${API_ENDPOINTS.settings.apiKeys}/${id}`,
      data
    );
    return response.data.data;
  }

  // Delete API key
  static async deleteApiKey(id: string): Promise<void> {
    await apiClient.delete(`${API_ENDPOINTS.settings.apiKeys}/${id}`);
  }

  // Regenerate API key
  static async regenerateApiKey(id: string): Promise<ApiKey> {
    const response = await apiClient.post<ApiResponse<ApiKey>>(
      `${API_ENDPOINTS.settings.apiKeys}/${id}/regenerate`
    );
    return response.data.data;
  }

  // Test API connection
  static async testConnection(): Promise<{ status: string; response_time: number }> {
    const startTime = Date.now();
    const response = await apiClient.get<ApiResponse<any>>('/api/health');
    const responseTime = Date.now() - startTime;
    
    return {
      status: response.data.success ? 'healthy' : 'error',
      response_time: responseTime,
    };
  }

  // Get system information
  static async getSystemInfo(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/api/system/info');
    return response.data.data;
  }

  // Export settings
  static async exportSettings(): Promise<Blob> {
    const response = await apiClient.get('/api/settings/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Import settings
  static async importSettings(file: File): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);

    await apiClient.post('/api/settings/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Reset settings to defaults
  static async resetToDefaults(): Promise<Settings> {
    const response = await apiClient.post<ApiResponse<Settings>>('/api/settings/reset');
    return response.data.data;
  }

  // Get audit logs
  static async getAuditLogs(params: {
    page?: number;
    size?: number;
    user_id?: string;
    action?: string;
    start_date?: string;
    end_date?: string;
  } = {}): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/api/settings/audit-logs', {
      params,
    });
    return response.data.data;
  }

  // Get backup status
  static async getBackupStatus(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/api/settings/backup/status');
    return response.data.data;
  }

  // Create backup
  static async createBackup(): Promise<{ backup_id: string; status: string }> {
    const response = await apiClient.post<ApiResponse<any>>('/api/settings/backup/create');
    return response.data.data;
  }

  // Restore from backup
  static async restoreBackup(backupId: string): Promise<void> {
    await apiClient.post(`/api/settings/backup/${backupId}/restore`);
  }
}